import { useEffect, useRef, useState } from 'react';

type UseViewportSpyOptions = {
  threshold?: number;
  freezeOnceVisible?: boolean;
  onChange?: (isVisible: boolean) => void;
};

const useViewportSpy = ({
  threshold = 0,
  freezeOnceVisible = false,
  onChange,
}: UseViewportSpyOptions) => {
  const [ref, setRef] = useState<Element | null>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);

  const callbackRef = useRef<UseViewportSpyOptions['onChange']>(undefined);

  callbackRef.current = onChange;

  const frozen = isVisible && freezeOnceVisible;

  useEffect(() => {
    if (!ref || !('IntersectionObserver' in window) || frozen) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((item) => {
          const nextIsVisible = item.isIntersecting;

          setIsVisible(nextIsVisible);

          if (callbackRef.current) {
            callbackRef.current(nextIsVisible);
          }
        });
      },
      { rootMargin: '0px', threshold },
    );

    observer.observe(ref);

    return () => {
      observer.disconnect();
    };
  }, [ref, frozen]);

  return { ref: setRef as (node?: Element | null) => void, isVisible };
};

export default useViewportSpy;
